import React, { useState, useRef, useEffect, useCallback } from "react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { Send, Sparkles, BarChart3, Loader2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  KnowledgebaseFilterProvider,
  useKnowledgebaseFilters,
} from "@/contexts/KnowledgebaseFilterContext";
import {
  ragApi,
  pollUntilComplete,
  QueryResponse,
  type QueryJobStatusResponse,
} from "@/services/api/rag";
import { QueryProgress } from "@/components/portal/QueryProgress";
import { ConversationSidebar } from "@/components/portal/shared/ConversationSidebar";
import { KnowledgebaseLayout } from "@/components/portal/shared/KnowledgebaseLayout";
import { RagAnswer } from "@/components/portal/shared/RagAnswer";
import { QueryFeedback } from "@/components/portal/shared/feedback";
import { useIsMobile } from "@/hooks/use-mobile";
import { AnswerLengthToggle } from "@/components/portal/knowledgebase/AnswerLengthToggle";
import {
  answerLengthParam,
  useAnswerLength,
} from "@/components/portal/knowledgebase/useAnswerLength";

interface QuerySearchParams {
  conversation_id?: string;
}

export const Route = createFileRoute("/portal/knowledgebase-file/query")({
  component: RAGQueryPageWrapper,
  validateSearch: (search: Record<string, unknown>): QuerySearchParams => ({
    conversation_id: typeof search.conversation_id === "string" ? search.conversation_id : undefined,
  }),
});

interface Message {
  id: string;
  type: "user" | "assistant";
  content: string;
  timestamp: Date;
  response?: QueryResponse;
}

// Wrapper to provide filter context
function RAGQueryPageWrapper() {
  return (
    <KnowledgebaseFilterProvider>
      <KnowledgebaseLayout>
        <RAGQueryPage />
      </KnowledgebaseLayout>
    </KnowledgebaseFilterProvider>
  );
}

function RAGQueryPage() {
  const { token, user } = useAuth();
  const { filters } = useKnowledgebaseFilters();
  const [answerLength, setAnswerLength] = useAnswerLength(user?.email);
  const navigate = useNavigate();
  const { conversation_id: urlConversationId } = useSearch({ from: Route.fullPath });
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  // Pipeline progress while the query runs. Cleared on every new submit so a
  // second question never briefly shows the first one's sources.
  const [progress, setProgress] = useState<QueryJobStatusResponse | null>(null);
  const [conversationId, setConversationId] = useState<string | undefined>(
    undefined,
  );
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const isMobile = useIsMobile();

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load conversation from URL param on mount
  const loadConversationFromUrl = useCallback(async (id: string) => {
    if (!token) return;
    try {
      const conversation = await ragApi.getConversation(token, id);
      setConversationId(id);
      // Convert conversation messages to our Message format
      const loadedMessages: Message[] = conversation.messages.map((m) => ({
        id: m.id,
        type: m.role as "user" | "assistant",
        content: m.content,
        timestamp: new Date(m.created_at),
        response: m.citations
          ? ({
              answer: m.content,
              query_id: m.query_id || "",
              citations: m.citations,
              intent: m.query_intent || "",
              intent_confidence: 0,
            } as QueryResponse)
          : undefined,
      }));
      setMessages(loadedMessages);
    } catch (error) {
      console.error("Failed to load conversation from URL:", error);
      toast.error("Conversation not found");
      setConversationId(undefined);
      setMessages([]);
      navigate({ to: Route.fullPath, search: {}, replace: true });
    }
  }, [token, navigate]);

  // Load conversation from URL param on initial mount
  useEffect(() => {
    if (!initialLoadDone && token && urlConversationId) {
      setInitialLoadDone(true);
      loadConversationFromUrl(urlConversationId);
    } else if (!initialLoadDone && token && !urlConversationId) {
      setInitialLoadDone(true);
    }
  }, [token, urlConversationId, initialLoadDone, loadConversationFromUrl]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputValue.trim() || loading || !token) return;

    const query = inputValue.trim();
    setInputValue("");

    // Add user message
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      type: "user",
      content: query,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    setProgress(null);

    try {
      // Enqueue the query (returns 202 with job_id)
      const enqueueResponse = await ragApi.executeQuery(token, {
        query_text: query,
        conversation_id: conversationId,
        category_ids:
          filters.selectedCategoryIds.length > 0
            ? filters.selectedCategoryIds
            : undefined,
        label_ids:
          filters.selectedLabelIds.length > 0
            ? filters.selectedLabelIds
            : undefined,
        latest_versions_only: filters.latestVersionsOnly,
        answer_length: answerLengthParam(answerLength),
      });

      // Poll until completed or failed. The `undefined`s take the defaults for
      // interval/timeout/error-tolerance — passed explicitly rather than
      // repeating their values, so a change to the defaults still applies here.
      const statusResponse = await pollUntilComplete(
        () => ragApi.getQueryJobStatus(token, enqueueResponse.job_id),
        (s) => s.status === "completed" || s.status === "failed",
        undefined,
        undefined,
        undefined,
        undefined,
        (s) => setProgress(s),
      );

      if (statusResponse.status === "failed") {
        throw new Error(statusResponse.error || "Query failed");
      }

      const response = statusResponse.result!;

      // Store conversation_id for subsequent queries
      if (response.conversation_id) {
        setConversationId(response.conversation_id);
        // Trigger sidebar refresh to show new/updated conversation
        setRefreshTrigger((prev) => prev + 1);
      }

      // Add assistant response
      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        type: "assistant",
        content: response.answer,
        timestamp: new Date(),
        response: response,
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Query failed:", error);
      toast.error("Failed to execute query", {
        description: "Please try again",
      });

      // Add error message
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        type: "assistant",
        content:
          "I'm sorry, I encountered an error processing your query. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      setProgress(null);
      inputRef.current?.focus();
    }
  };

  // Handle new conversation
  const handleNewConversation = () => {
    setConversationId(undefined);
    setMessages([]);
    inputRef.current?.focus();
  };

  // Handle selecting a conversation from sidebar
  const handleSelectConversation = async (id: string | undefined) => {
    if (!id || !token) {
      handleNewConversation();
      return;
    }

    try {
      const conversation = await ragApi.getConversation(token, id);
      setConversationId(id);
      // Convert conversation messages to our Message format
      const loadedMessages: Message[] = conversation.messages.map((m) => ({
        id: m.id,
        type: m.role as "user" | "assistant",
        content: m.content,
        timestamp: new Date(m.created_at),
        response: m.citations
          ? ({
              answer: m.content,
              query_id: m.query_id || "",
              citations: m.citations,
              intent: m.query_intent || "",
              intent_confidence: 0,
            } as QueryResponse)
          : undefined,
      }));
      setMessages(loadedMessages);
    } catch (error) {
      console.error("Failed to load conversation:", error);
      toast.error("Conversation not found");
      handleNewConversation();
    }
  };

  const conversationSidebar = (
    <ConversationSidebar
      currentConversationId={conversationId}
      onSelectConversation={(id) => {
        handleSelectConversation(id);
        setMobileSidebarOpen(false);
      }}
      onNewConversation={() => {
        handleNewConversation();
        setMobileSidebarOpen(false);
      }}
      refreshTrigger={refreshTrigger}
    />
  );

  return (
    <div className="flex h-full">
      {/* Conversation Sidebar — Desktop inline, Mobile Sheet */}
      {isMobile ? (
        <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
          <SheetContent side="left" className="w-80 p-0">
            <SheetHeader className="sr-only">
              <SheetTitle>Conversations</SheetTitle>
              <SheetDescription>Your conversation history</SheetDescription>
            </SheetHeader>
            {conversationSidebar}
          </SheetContent>
        </Sheet>
      ) : (
        <div className="w-80 flex-shrink-0">
          {conversationSidebar}
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 p-3 md:p-6">
        {/* Header */}
        <div className="mb-4 md:mb-6 flex items-center gap-3">
          {isMobile && (
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 flex-shrink-0"
              onClick={() => setMobileSidebarOpen(true)}
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          )}
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900">Query Assistant</h1>
            <p className="mt-1 md:mt-2 text-sm md:text-lg text-slate-600">
              Ask questions about your documents. Powered by multi-LLM consensus
              and semantic search.
            </p>
          </div>
        </div>

        {/* Conversation area */}
        <Card className="flex-1 flex flex-col min-h-0">
          <CardHeader className="py-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4" />
              Conversation
            </CardTitle>
            <CardDescription className="text-xs">
              Powered by intent classification and adaptive retrieval
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col min-h-0 pt-0">
            {/* Messages */}
            <ScrollArea ref={scrollRef} className="flex-1 pr-4">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <Sparkles className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    Start a conversation
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 max-w-md">
                    Ask questions about your documents. I'll use semantic search
                    and multi-LLM consensus to provide accurate answers with
                    citations.
                  </p>
                  <div className="grid gap-2 text-left max-w-md">
                    <p className="text-xs text-muted-foreground font-medium">
                      Example queries:
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="justify-start text-left h-auto py-2"
                      onClick={() =>
                        setInputValue("What are the key metrics for Q4?")
                      }
                    >
                      "What are the key metrics for Q4?"
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="justify-start text-left h-auto py-2"
                      onClick={() =>
                        setInputValue(
                          "Explain the company's remote work policy",
                        )
                      }
                    >
                      "Explain the company's remote work policy"
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="justify-start text-left h-auto py-2"
                      onClick={() =>
                        setInputValue("Compare Plan A vs Plan B features")
                      }
                    >
                      "Compare Plan A vs Plan B features"
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-4 pb-4">
                {messages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    renderContent={() =>
                      message.type === "assistant" && message.response ? (
                        <RagAnswer
                          content={message.content}
                          citations={message.response.citations}
                        />
                      ) : (
                        <p className="text-sm whitespace-pre-wrap">
                          {message.content}
                        </p>
                      )
                    }
                  />
                ))}

                {loading && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <Sparkles className="h-4 w-4 text-primary-foreground" />
                    </div>
                    <div className="flex-1">
                      <QueryProgress
                        stage={progress?.stage}
                        sources={progress?.sources}
                      />
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Input */}
            <form onSubmit={handleSubmit} className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Answer length</span>
                <AnswerLengthToggle
                  value={answerLength}
                  onChange={setAnswerLength}
                  disabled={loading}
                />
              </div>
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Ask a question about your documents..."
                  disabled={loading}
                  className="flex-1"
                />
                <Button type="submit" disabled={loading || !inputValue.trim()}>
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Message bubble component
function MessageBubble({
  message,
  renderContent,
}: {
  message: Message;
  renderContent: () => React.ReactNode;
}) {
  const isUser = message.type === "user";

  return (
    <div className={`flex items-start gap-3 ${isUser ? "justify-end" : ""}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
          <Sparkles className="h-4 w-4 text-primary-foreground" />
        </div>
      )}

      <div className={`flex-1 max-w-[80%] ${isUser ? "order-first" : ""}`}>
        <div
          className={`rounded-lg p-3 ${
            isUser ? "bg-primary text-primary-foreground ml-auto" : "bg-muted"
          }`}
        >
          {renderContent()}
        </div>

        {/* Show intent badge and feedback for assistant responses */}
        {!isUser && message.response && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <IntentBadge response={message.response} />
            {message.response.query_id && (
              <QueryFeedback
                queryType="rag"
                queryId={message.response.query_id}
                compact
              />
            )}
          </div>
        )}
      </div>

      {isUser && (
        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-medium">You</span>
        </div>
      )}
    </div>
  );
}

// Intent badge component
function IntentBadge({ response }: { response: QueryResponse }) {
  const getIntentColor = (intentType: string) => {
    switch (intentType) {
      case "TABLE_FOCUSED":
        return "bg-blue-100 text-blue-700";
      case "TEXT_FOCUSED":
        return "bg-green-100 text-green-700";
      case "COMPARISON":
        return "bg-purple-100 text-purple-700";
      case "HYBRID":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (!response.intent) {
    return null;
  }

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <Badge
          variant="outline"
          className={`text-[10px] cursor-help ${getIntentColor(response.intent)}`}
        >
          <BarChart3 className="h-3 w-3 mr-1" />
          {response.intent.replace("_", " ")}
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent className="w-80" side="top">
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Query Intent Analysis</h4>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Confidence:</span>
              <span className="font-medium">
                {(response.intent_confidence * 100).toFixed(0)}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Table Ratio:</span>
              <span className="font-medium">
                {(response.table_ratio * 100).toFixed(0)}%
              </span>
            </div>
          </div>
          {response.intent_reasoning && (
            <>
              <Separator />
              <p className="text-xs text-muted-foreground">
                {response.intent_reasoning}
              </p>
            </>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

